import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import { toast } from "react-toastify";
import { API_ENDPOINT } from "../../utils/constants";
import {
  getErrorResponse,
  getHeader,
  getMultipartHeader,
} from "../../utils/util";
import PageTitle from "../../widgets/PageTitle";

const EMPTY_FORM = {
  name: "",
  employeeId: "",
  profilePic: null,
  profilePicUrl: "",
};

const MAX_IMAGE_SIZE = 8 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const EMPLOYEE_ID_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,39}$/;

const ManageEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [generatingId, setGeneratingId] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [statusChangingId, setStatusChangingId] = useState("");
  const [assignmentEmployee, setAssignmentEmployee] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const { data } = await axios.get(
        `${API_ENDPOINT}/root/employees`,
        getHeader()
      );
      setEmployees(data.employees ?? []);
    } catch (error) {
      setLoadError(getErrorResponse(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const summary = useMemo(
    () => ({
      total: employees.length,
      active: employees.filter((employee) => employee.status === "active")
        .length,
      assigned: employees.reduce(
        (sum, employee) => sum + Number(employee.assignedClientCount || 0),
        0
      ),
    }),
    [employees]
  );

  const openCreateDialog = () => {
    setEditingEmployee(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setFormOpen(true);
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setForm({
      name: employee.name,
      employeeId: employee.employeeId,
      profilePic: null,
      profilePicUrl: employee.profilePic,
    });
    setErrors({});
    setFormOpen(true);
  };

  const closeFormDialog = () => {
    if (saving) return;
    setFormOpen(false);
    setEditingEmployee(null);
    setForm(EMPTY_FORM);
    setErrors({});
  };

  const validateForm = () => {
    const nextErrors = {};
    const cleanName = form.name.trim();
    const cleanEmployeeId = form.employeeId.trim().toUpperCase();

    if (!cleanName) {
      nextErrors.name = "Formal name is required";
    } else if (cleanName.length < 2 || cleanName.length > 100) {
      nextErrors.name = "Formal name must be between 2 and 100 characters";
    }

    if (!cleanEmployeeId) {
      nextErrors.employeeId = "Employee ID is required";
    } else if (!EMPLOYEE_ID_PATTERN.test(cleanEmployeeId)) {
      nextErrors.employeeId =
        "Use 3–40 uppercase letters, numbers, hyphens, or underscores";
    }

    if (!form.profilePic && !form.profilePicUrl) {
      nextErrors.profilePic = "Profile picture is required";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setErrors((current) => ({
        ...current,
        profilePic: "Choose a JPG, PNG, or WebP image",
      }));
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setErrors((current) => ({
        ...current,
        profilePic: "Profile picture must be 8 MB or smaller",
      }));
      return;
    }

    setForm((current) => ({ ...current, profilePic: file }));
    setErrors((current) => ({ ...current, profilePic: "" }));
  };

  const generateEmployeeId = async () => {
    try {
      setGeneratingId(true);
      const { data } = await axios.get(
        `${API_ENDPOINT}/root/employees/generate-id`,
        getHeader()
      );
      setForm((current) => ({ ...current, employeeId: data.employeeId }));
      setErrors((current) => ({ ...current, employeeId: "" }));
    } catch (error) {
      toast.error(getErrorResponse(error));
    } finally {
      setGeneratingId(false);
    }
  };

  const saveEmployee = async () => {
    if (!validateForm()) return;

    const payload = new FormData();
    payload.append("name", form.name.trim());
    payload.append("employeeId", form.employeeId.trim().toUpperCase());
    if (form.profilePic) payload.append("profilePic", form.profilePic);

    try {
      setSaving(true);
      const request = editingEmployee
        ? axios.put(
            `${API_ENDPOINT}/root/employees/${editingEmployee._id}`,
            payload,
            getMultipartHeader()
          )
        : axios.post(
            `${API_ENDPOINT}/root/employees`,
            payload,
            getMultipartHeader()
          );
      const { data } = await request;
      toast.success(data.message);
      closeFormDialog();
      await fetchEmployees();
    } catch (error) {
      toast.error(getErrorResponse(error));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (employee) => {
    const nextStatus = employee.status === "active" ? "inactive" : "active";
    try {
      setStatusChangingId(employee._id);
      const { data } = await axios.patch(
        `${API_ENDPOINT}/root/employees/${employee._id}/status`,
        { status: nextStatus },
        getHeader()
      );
      toast.success(data.message);
      await fetchEmployees();
    } catch (error) {
      toast.error(getErrorResponse(error));
    } finally {
      setStatusChangingId("");
    }
  };

  const deleteEmployee = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const { data } = await axios.delete(
        `${API_ENDPOINT}/root/employees/${deleteTarget._id}`,
        getHeader()
      );
      toast.success(data.message);
      setDeleteTarget(null);
      await fetchEmployees();
    } catch (error) {
      toast.error(getErrorResponse(error));
    } finally {
      setDeleting(false);
    }
  };

  const openAssignments = async (employee) => {
    try {
      setAssignmentEmployee(employee);
      setAccounts([]);
      setSelectedAccounts([]);
      setAccountsLoading(true);
      const { data } = await axios.get(
        `${API_ENDPOINT}/root/employees/accounts`,
        getHeader()
      );
      const accountList = data.accounts ?? [];
      setAccounts(accountList);
      setSelectedAccounts(
        accountList.filter(
          (account) => account.assignedToEmployee === employee.employeeId
        )
      );
    } catch (error) {
      toast.error(getErrorResponse(error));
      setAssignmentEmployee(null);
    } finally {
      setAccountsLoading(false);
    }
  };

  const saveAssignments = async () => {
    if (!assignmentEmployee) return;
    try {
      setAssigning(true);
      const { data } = await axios.put(
        `${API_ENDPOINT}/root/employees/${assignmentEmployee._id}/assignments`,
        { customerIds: selectedAccounts.map((account) => account.customerId) },
        getHeader()
      );
      toast.success(data.message);
      setAssignmentEmployee(null);
      await fetchEmployees();
    } catch (error) {
      toast.error(getErrorResponse(error));
    } finally {
      setAssigning(false);
    }
  };

  const profilePreview = useMemo(
    () =>
      form.profilePic
        ? URL.createObjectURL(form.profilePic)
        : form.profilePicUrl,
    [form.profilePic, form.profilePicUrl]
  );

  useEffect(
    () => () => {
      if (profilePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(profilePreview);
      }
    },
    [profilePreview]
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <PageTitle>Manage Employees</PageTitle>
          <Typography color="text.secondary" variant="body2" mt={0.5}>
            Maintain employees and assign each client to one employee.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddOutlinedIcon />}
          onClick={openCreateDialog}
        >
          Add employee
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3}>
        {[
          { label: "Employees", value: summary.total, icon: <PersonOutlineIcon /> },
          { label: "Active", value: summary.active, icon: <TaskAltOutlinedIcon /> },
          {
            label: "Assigned clients",
            value: summary.assigned,
            icon: <GroupOutlinedIcon />,
          },
        ].map((item) => (
          <Paper key={item.label} variant="outlined" sx={{ p: 2, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box color="primary.main" display="flex">
                {item.icon}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.1}>
                  {item.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.label}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={fetchEmployees}>
            Retry
          </Button>
        }>
          {loadError}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && (
          <Table size="small" aria-label="Employees">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Employee ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Clients
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      No employees have been added yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                employees.map((employee) => (
                  <TableRow hover key={employee._id}>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar src={employee.profilePic} alt={employee.name} />
                        <Typography fontWeight={600}>{employee.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {employee.employeeId}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        startIcon={<GroupOutlinedIcon />}
                        onClick={() => openAssignments(employee)}
                        disabled={employee.status !== "active"}
                      >
                        {employee.assignedClientCount || 0}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={employee.status === "active" ? "success" : "default"}
                        label={employee.status === "active" ? "Active" : "Inactive"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Assign clients">
                        <IconButton
                          size="small"
                          onClick={() => openAssignments(employee)}
                          disabled={employee.status !== "active"}
                        >
                          <GroupOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit employee">
                        <IconButton size="small" onClick={() => openEditDialog(employee)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={employee.status === "active" ? "Deactivate" : "Activate"}
                      >
                        <span>
                          <IconButton
                            size="small"
                            color={employee.status === "active" ? "warning" : "success"}
                            onClick={() => changeStatus(employee)}
                            disabled={statusChangingId === employee._id}
                          >
                            {statusChangingId === employee._id ? (
                              <CircularProgress size={18} />
                            ) : employee.status === "active" ? (
                              <PersonOffOutlinedIcon fontSize="small" />
                            ) : (
                              <TaskAltOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete employee">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(employee)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={formOpen} onClose={closeFormDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editingEmployee ? "Edit employee" : "Add employee"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <TextField
              required
              autoFocus
              label="Formal name"
              value={form.name}
              onChange={(event) => {
                setForm((current) => ({ ...current, name: event.target.value }));
                setErrors((current) => ({ ...current, name: "" }));
              }}
              error={Boolean(errors.name)}
              helperText={
                errors.name || "Use the employee’s formal name as it should appear in records."
              }
              inputProps={{ maxLength: 100 }}
              fullWidth
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
              <TextField
                required
                label="Employee ID"
                value={form.employeeId}
                onChange={(event) => {
                  setForm((current) => ({
                    ...current,
                    employeeId: event.target.value.toUpperCase(),
                  }));
                  setErrors((current) => ({ ...current, employeeId: "" }));
                }}
                error={Boolean(errors.employeeId)}
                helperText={
                  errors.employeeId ||
                  "Enter a friendly ID or generate a Smart Growth AI employee ID."
                }
                inputProps={{ maxLength: 40 }}
                fullWidth
              />
              <LoadingButton
                variant="outlined"
                loading={generatingId}
                startIcon={<AutorenewOutlinedIcon />}
                onClick={generateEmployeeId}
                sx={{ minWidth: 130, mt: { sm: 0.5 } }}
              >
                Generate
              </LoadingButton>
            </Stack>

            <Divider />
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar src={profilePreview} alt="Profile preview" sx={{ width: 64, height: 64 }} />
              <Box>
                <Button component="label" variant="outlined" startIcon={<UploadOutlinedIcon />}>
                  Choose profile picture
                  <input
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageChange}
                  />
                </Button>
                <Typography
                  variant="caption"
                  display="block"
                  mt={0.75}
                  color={errors.profilePic ? "error" : "text.secondary"}
                >
                  {errors.profilePic || "Required. JPG, PNG, or WebP up to 8 MB."}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeFormDialog} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton variant="contained" loading={saving} onClick={saveEmployee}>
            {editingEmployee ? "Save changes" : "Add employee"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(assignmentEmployee)}
        onClose={() => !assigning && setAssignmentEmployee(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Assign clients to {assignmentEmployee?.name}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Each client can be assigned to only one employee. Selecting a client already
            assigned elsewhere will move that client to this employee.
          </Alert>
          <Autocomplete
            multiple
            disableCloseOnSelect
            loading={accountsLoading}
            options={accounts}
            value={selectedAccounts}
            onChange={(_, value) => setSelectedAccounts(value)}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            getOptionLabel={(option) => option.customerId}
            renderOption={(props, option) => (
              <li {...props} key={option._id}>
                <Box>
                  <Typography variant="body2">{option.customerId}</Typography>
                  {option.assignedToEmployee &&
                    option.assignedToEmployee !== assignmentEmployee?.employeeId && (
                      <Typography variant="caption" color="warning.main">
                        Currently assigned to {option.assignedToEmployee}
                      </Typography>
                    )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Clients"
                placeholder="Search customer IDs"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {accountsLoading && <CircularProgress size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            {selectedAccounts.length} client{selectedAccounts.length === 1 ? "" : "s"} selected
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignmentEmployee(null)} disabled={assigning}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={assigning}
            onClick={saveAssignments}
            disabled={accountsLoading}
          >
            Save assignments
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Delete employee?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete {deleteTarget?.name}? Their assigned clients will become unassigned.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <LoadingButton color="error" loading={deleting} onClick={deleteEmployee}>
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageEmployees;
